/**
 * A stock's newest candlestick pattern as a row of figures: what the
 * Scanner's "Candlesticks" columns show and what a Backtest Lab criterion
 * tests.
 *
 * The figures are of ONE pattern: the newest one within the bars kept
 * (`SCAN.keepSessions`) that has a side and is not a single bar that fires
 * anywhere, with how many bars ago it was. Both readers come through
 * `columnsOf`, so a stock reads the same in the Scanner, in a backtest row and
 * on the Candlesticks page.
 *
 * Null is not measured (the stock was not read), never "no pattern": a stock
 * that was read and has none carries 0 in the two flags and null in the rest.
 *
 * The weekly columns (`cdlw_*`) are the same figures of the stock's newest
 * pattern on completed weekly candles, within the weeks kept
 * (`SCAN.keepWeeks`): `weeklyOf` renames a row, so the two can never mean
 * different things. A bearish pattern is not scored on weekly candles
 * (`SCORE.unscored`), so its `cdlw_score` is null, which `weeklyScoreWhy`
 * tells from a stock not read.
 *
 * Pure: relative imports only, and none of them the chart library's, because
 * the Scanner's row type (and so the browser) imports this module.
 */
import { isMinor, patternOf, placeOf, PATTERNS } from "./catalog";
import { confirmOf, levelsOf, type Confirm, type Place } from "./context";
import type { CBar } from "./detect";
import { SCAN, frameOfCandles, unscoredWhy } from "./rules";

export type CandleColumns = {
  /** 1 when the newest pattern is bullish, 0 when there is none or it is bearish. */
  cdl_bull: number | null;
  /** 1 when the newest pattern is bearish, 0 when there is none or it is bullish. */
  cdl_bear: number | null;
  /** Which pattern: its number in the catalog, from 1 (`CANDLE_PATTERN_CHOICES`). */
  cdl_pattern: number | null;
  /** Its score, 0 to 100. Under the weekly name, null for a bearish pattern too: not scored on weekly candles (`weeklyScoreWhy`). */
  cdl_score: number | null;
  /** Bars since its last candle: 0 is the newest completed bar. */
  cdl_ago: number | null;
  /** Candles it is made of. */
  cdl_bars: number | null;
  /** 1 a reversal, 0 a continuation. */
  cdl_reversal: number | null;
  /** Its standing with the bars since: 1 confirmed, -1 failed, 0 awaiting or lapsed. */
  cdl_standing: number | null;
  /** The close that confirms it. */
  cdl_level: number | null;
  /** The latest price over that close, percent: past it when positive for a bullish pattern, negative for a bearish one. */
  cdl_to_level: number | null;
  /** 1 when it happened at a level (an extreme, an average, outside the band). */
  cdl_placed: number | null;
  /** 1 with the larger trend, -1 against it, 0 neither. */
  cdl_trend: number | null;
  /** Its last candle's volume over the average of the 50 bars before. */
  cdl_volume: number | null;
};

export const CANDLE_COLUMN_IDS = [
  "cdl_bull",
  "cdl_bear",
  "cdl_pattern",
  "cdl_score",
  "cdl_ago",
  "cdl_bars",
  "cdl_reversal",
  "cdl_standing",
  "cdl_level",
  "cdl_to_level",
  "cdl_placed",
  "cdl_trend",
  "cdl_volume",
] as const;

/** A stock that was not read: every figure not measured. */
export const EMPTY_CANDLE_COLUMNS: CandleColumns = {
  cdl_bull: null,
  cdl_bear: null,
  cdl_pattern: null,
  cdl_score: null,
  cdl_ago: null,
  cdl_bars: null,
  cdl_reversal: null,
  cdl_standing: null,
  cdl_level: null,
  cdl_to_level: null,
  cdl_placed: null,
  cdl_trend: null,
  cdl_volume: null,
};

/** A stock that was read and has no pattern in the bars kept. */
export const NO_CANDLE_PATTERN: CandleColumns = { ...EMPTY_CANDLE_COLUMNS, cdl_bull: 0, cdl_bear: 0 };

/** A row of columns under the weekly names: `cdl_score` is `cdlw_score`. */
type Weekly<T> = { [K in keyof T as K extends `cdl_${infer R}` ? `cdlw_${R}` : never]: T[K] };
export type WeeklyCandleColumns = Weekly<CandleColumns>;

export const WEEKLY_CANDLE_COLUMN_IDS = CANDLE_COLUMN_IDS.map((k) => `cdlw_${k.slice(4)}`) as ReadonlyArray<keyof WeeklyCandleColumns>;

/** A row of columns read on weekly candles, under the weekly names. */
export function weeklyOf(c: CandleColumns): WeeklyCandleColumns {
  const out: Record<string, number | null> = {};
  for (const k of CANDLE_COLUMN_IDS) out[`cdlw_${k.slice(4)}`] = c[k];
  return out as WeeklyCandleColumns;
}

/**
 * Why a row's weekly score is not measured though its weekly pattern was
 * read: the pattern's side is not scored on weekly candles (`SCORE.unscored`,
 * a bearish one, through `frameOfCandles`), the reason in the page's words.
 * Null where the score is measured, where no pattern was read, and on a side
 * that is scored. The Scanner's cell says "not scored" with this on hover.
 */
export function weeklyScoreWhy(c: { cdlw_bull?: number | null; cdlw_bear?: number | null; cdlw_score?: number | null }): string | null {
  if (c.cdlw_score != null) return null;
  const bias = c.cdlw_bull === 1 ? "bull" : c.cdlw_bear === 1 ? "bear" : null;
  return bias == null ? null : unscoredWhy(frameOfCandles(false, null, "1w"), bias);
}

/** A stock whose weekly candles were not read: too few weeks, or not read at all. */
export const EMPTY_WEEKLY_CANDLE_COLUMNS: WeeklyCandleColumns = weeklyOf(EMPTY_CANDLE_COLUMNS);
/** A stock whose weekly candles were read and have no pattern in the weeks kept. */
export const NO_WEEKLY_CANDLE_PATTERN: WeeklyCandleColumns = weeklyOf(NO_CANDLE_PATTERN);

/** The patterns as a menu's choices: a name and the number `cdl_pattern` carries. */
export const CANDLE_PATTERN_CHOICES: ReadonlyArray<{ label: string; value: number }> = PATTERNS.map((p, i) => ({ label: p.name, value: i + 1 }));

/** What `columnsOf` needs of a pattern, however it was read. */
export type ColumnHit = {
  /** Bars ago of its last candle. */
  a: number;
  /** The patterns ending on that bar, the naming one first. */
  p: readonly string[];
  /** Its score; null where its side is not scored (`SCORE.unscored`): of the bars these columns read, a bearish pattern on weekly candles. */
  s: number | null;
  lo: number;
  hi: number;
  /** Its last candle's volume over the average. */
  vr: number | null;
  tr: -1 | 0 | 1 | null;
  at: readonly Place[] | null;
  c: readonly [Confirm["state"], number] | null;
};

/** Whether a pattern is one the columns speak of: it has a side and is not a minor bar. */
export function columnWorthy(id: string): boolean {
  const def = patternOf(id);
  return !!def && def.bias !== "neutral" && !isMinor(id);
}

/** The newest of a stock's patterns the columns speak of, from a list that is newest first. */
export function newestWorthy<H extends Pick<ColumnHit, "p">>(hits: readonly H[] | null | undefined): H | null {
  for (const h of hits ?? []) if (columnWorthy(h.p[0] ?? "")) return h;
  return null;
}

/**
 * The columns of a pattern at a price. `price` is the latest price the row is
 * read at; without one the distance to the level is not measured.
 */
export function columnsOf(hit: ColumnHit | null, price: number | null | undefined): CandleColumns {
  const def = hit ? patternOf(hit.p[0] ?? "") : null;
  if (!hit || !def || def.bias === "neutral") return NO_CANDLE_PATTERN;
  const dir = def.bias === "bull" ? 1 : -1;
  const level = levelsOf(hit.lo, hit.hi, def.bias)?.trigger ?? null;
  const state = hit.c?.[0];
  return {
    cdl_bull: dir > 0 ? 1 : 0,
    cdl_bear: dir < 0 ? 1 : 0,
    cdl_pattern: placeOf(def.id) + 1,
    cdl_score: hit.s,
    cdl_ago: hit.a,
    cdl_bars: def.bars,
    cdl_reversal: def.kind === "reversal" ? 1 : 0,
    cdl_standing: state === "confirmed" ? 1 : state === "failed" ? -1 : 0,
    cdl_level: level,
    cdl_to_level: level != null && level > 0 && price != null && price > 0 ? (price / level - 1) * 100 : null,
    cdl_placed: hit.at == null ? null : hit.at.length > 0 ? 1 : 0,
    cdl_trend: hit.tr == null ? null : hit.tr === dir ? 1 : hit.tr === 0 ? 0 : -1,
    cdl_volume: hit.vr,
  };
}

/** What a reader of a whole series needs of a pattern read in it (`PatternRead`, read.ts). */
export type SeriesHit = {
  i: number;
  ids: readonly string[];
  bias: "bull" | "bear" | "neutral";
  score: number | null;
  ctx: { lo: number; hi: number; vol: number | null; trend: -1 | 0 | 1 | null; at: Place[] | null };
};

/**
 * The columns of a series AS OF bar `i`, for a backtest's row of that day:
 * the newest pattern ending on bars `i - keep + 1` to `i`, its standing
 * settled by the bars up to `i` and none after, at that bar's close unless a
 * `price` is handed in (a weekly read takes the day's). `hits` are a series'
 * patterns oldest first (`readPatterns`).
 */
export function columnsAsOf(bars: readonly CBar[], hits: readonly SeriesHit[], i: number, keep: number = SCAN.keepSessions, price?: number): CandleColumns {
  if (i < 0 || i >= bars.length) return EMPTY_CANDLE_COLUMNS;
  // The newest pattern at or before the bar, walking back from the end of the list.
  let k = hits.length - 1;
  while (k >= 0 && hits[k]!.i > i) k -= 1;
  for (; k >= 0 && i - hits[k]!.i < keep; k--) {
    const h = hits[k]!;
    if (!columnWorthy(h.ids[0] ?? "")) continue;
    const confirm = confirmOf(bars, h.i, h.ctx.lo, h.ctx.hi, h.bias, i);
    return columnsOf({ a: i - h.i, p: h.ids, s: h.score, lo: h.ctx.lo, hi: h.ctx.hi, vr: h.ctx.vol, tr: h.ctx.trend, at: h.ctx.at, c: confirm ? [confirm.state, confirm.after] : null }, price ?? bars[i]!.c);
  }
  return NO_CANDLE_PATTERN;
}
